import { useEffect, useState } from "react";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { whenIn } from "../system/format";

// ===========================================================================
// Sweep §4 / 0199. WHAT THE AGENCY TOLD THIS SHIFT'S CREW, on the worker's shift.
//
//  1. OPENING THE SHIFT IS READING IT. The card marks what it shows as read, once,
//     after it has drawn it — that is the receipt the agency sees. A message that
//     was new when the card loaded keeps its "New" chip for this visit, so the
//     receipt does not hide the thing it records.
//
//  2. NO MESSAGES IS NOT AN ERROR. Most shifts have none; the card renders nothing,
//     as the briefing card does. A failed read also renders nothing rather than a
//     red card above the shift: the message is never the only place the worker can
//     learn the call time, and the email said there was one.
//
//  3. THE TEXT IS THE AGENCY'S AND IS SHOWN AS WRITTEN. It is not translated.
// ===========================================================================

type ShiftMessage = { id: string; body: string; sent_at: string; read_at: string | null };

export function ShiftMessages({
  assignmentId,
  apiBase,
  orgZone,
}: {
  assignmentId: string;
  apiBase: string;
  orgZone: string | null;
}) {
  const { t } = useT();
  const [messages, setMessages] = useState<ShiftMessage[]>([]);

  useEffect(() => {
    let live = true;
    void (async () => {
      const { status, body } = await send<{ messages?: ShiftMessage[] }>(console_.myShiftMessages(apiBase, assignmentId));
      if (!live || status !== 200 || !Array.isArray(body?.messages)) return;
      setMessages(body.messages);
      if (body.messages.some((m) => m.read_at === null)) {
        await send(console_.openMyShiftMessages(apiBase, assignmentId));
      }
    })();
    return () => {
      live = false;
    };
  }, [apiBase, assignmentId]);

  if (messages.length === 0) return null;

  return (
    <section className="worker-card">
      <h2>{t("Messages from the agency")}</h2>
      <ul className="plain-list">
        {messages.map((m) => (
          <li key={m.id} className="stack">
            <p className="worker-card-head">
              {m.read_at === null ? <StatusChip tone="attention" label={t("New")} /> : null}
              <span className="muted">{t("Sent {when}", { when: whenIn(m.sent_at, orgZone) })}</span>
            </p>
            <p>{m.body}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
